import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RatingPicker } from "@/components/StarRating";

const COMMENT_MAX = 500;

/**
 * Post-watch (or any-time) rating prompt. Fetches the viewer's existing
 * rating on open so re-submitting acts as an edit instead of a fresh
 * insert. On submit success, calls ``onSubmitted(rating)`` with the fresh
 * rating so the parent can refresh its local summary count.
 */
export default function RatingDialog({ open, onOpenChange, film, onSubmitted }) {
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  // On open, fetch the viewer's existing rating (if any) so the picker
  // prefills — a viewer editing a review shouldn't have to type again.
  useEffect(() => {
    if (!open || !film?.id) return;
    let cancelled = false;
    setLoading(true);
    setStars(0);
    setComment("");
    setIsEdit(false);
    api.get(`/my/ratings/${film.id}`)
      .then((r) => {
        if (cancelled) return;
        if (r.data && r.data.stars) {
          setStars(r.data.stars);
          setComment(r.data.comment || "");
          setIsEdit(true);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, film?.id]);

  const submit = async () => {
    if (!stars) {
      toast.error("Pick a star rating first.");
      return;
    }
    if (comment.length > COMMENT_MAX) {
      toast.error(`Keep your review under ${COMMENT_MAX} characters.`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/ratings", {
        film_id: film.id,
        stars,
        comment: comment.trim(),
      });
      toast.success(isEdit ? "Review updated. Thanks!" : "Thanks for rating this film!");
      onSubmitted?.(data);
      onOpenChange?.(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save rating");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-white/10 text-foreground sm:max-w-md" data-testid="rating-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {isEdit ? "Update your review" : "Rate this film"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <p className="text-sm text-foreground/70 leading-relaxed">
            Your rating helps <strong className="text-foreground/95">{film?.filmmaker_name || "the filmmaker"}</strong>{" "}
            know what landed. Reviews stay attached to your Cinevoya name.
          </p>

          <div className="flex flex-col items-center gap-2 py-2">
            <RatingPicker value={stars} onChange={setStars} disabled={submitting || loading} />
            <p className="text-xs text-foreground/50 h-4" data-testid="rating-star-label">
              {stars === 0 && "Tap a star"}
              {stars === 1 && "Not for me"}
              {stars === 2 && "It was okay"}
              {stars === 3 && "Solid watch"}
              {stars === 4 && "Really enjoyed it"}
              {stars === 5 && "A must-watch"}
            </p>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-foreground/50 font-semibold" htmlFor="rating-comment">
              A quick note (optional)
            </label>
            <Textarea
              id="rating-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, COMMENT_MAX))}
              placeholder="What did you love? What could be better?"
              className="bg-ink border-white/10 mt-1.5 min-h-[90px] resize-none"
              disabled={submitting || loading}
              data-testid="rating-comment-input"
            />
            <p className="text-[10px] text-foreground/40 mt-1 text-right">{comment.length} / {COMMENT_MAX}</p>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => onOpenChange?.(false)}
              disabled={submitting}
              className="text-foreground/70 hover:bg-white/5"
              data-testid="rating-cancel-btn"
            >
              Maybe later
            </Button>
            <Button
              onClick={submit}
              disabled={submitting || loading || !stars}
              className="bg-terracotta hover:bg-ember text-white"
              data-testid="rating-submit-btn"
            >
              {submitting ? "Saving…" : isEdit ? "Update review" : "Submit review"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
